// Parks a kundli-matching draft across the login step.
//
// The compatibility calculator gates its score behind a phone number, and the
// login modal offers Google alongside OTP. Google sign-in is a FULL PAGE
// RELOAD, so without this the visitor fills in two complete sets of birth
// details, taps the CTA, signs in, and lands back on an empty calculator with
// everything they typed gone. That is the worst possible moment to lose it:
// immediately after asking them for their phone number.
//
// sessionStorage, not localStorage: a half-finished match should not still be
// sitting there tomorrow.

import type { Birth } from "../utils/calculatorApi";

export const COMPAT_DRAFT_KEY = "auraCompatMatchDraft";

// Long enough to cover an OTP round trip plus a slow redirect, short enough
// that a forgotten tab does not resurrect stale details.
const MAX_AGE_MS = 60 * 60 * 1000;

/** One side of the match: the birth itself plus the place fields a profile needs. */
export interface DraftSide {
  birth: Birth;
  place: string;
  /** Whose chart this is. Becomes the user_profiles name for the compat chat. */
  name: string;
  /** IANA zone, e.g. "Asia/Kolkata". Needed by user_birth_details.birth_timezone. */
  timezone?: string;
}

export interface CompatDraft {
  boy: DraftSide;
  girl: DraftSide;
}

function validSide(s: any): s is DraftSide {
  return (
    s &&
    s.birth &&
    typeof s.birth.date === "string" &&
    typeof s.birth.time === "string" &&
    typeof s.birth.lat === "number" &&
    typeof s.birth.lon === "number" &&
    typeof s.place === "string" &&
    typeof s.name === "string"
  );
}

export function saveCompatDraft(draft: CompatDraft) {
  try {
    sessionStorage.setItem(
      COMPAT_DRAFT_KEY,
      JSON.stringify({ ...draft, ts: Date.now() }),
    );
  } catch {
    /* storage blocked: they re-enter the details after signing in */
  }
}

/**
 * Read AND clear the parked draft.
 *
 * Single-use, so a remount or a StrictMode double effect cannot replay the same
 * match twice. Both sides are validated because this survives a page reload and
 * a half-written entry would otherwise reach the scoring call as NaN.
 */
export function takeCompatDraft(): CompatDraft | null {
  try {
    const raw = sessionStorage.getItem(COMPAT_DRAFT_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(COMPAT_DRAFT_KEY);
    const parsed = JSON.parse(raw);
    if (!parsed?.ts || Date.now() - parsed.ts > MAX_AGE_MS) return null;
    if (!validSide(parsed.boy) || !validSide(parsed.girl)) return null;
    return { boy: parsed.boy, girl: parsed.girl };
  } catch {
    return null;
  }
}

export function clearCompatDraft() {
  try {
    sessionStorage.removeItem(COMPAT_DRAFT_KEY);
  } catch {
    /* nothing to do */
  }
}
